import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  constructor(
    private authService: AuthService,
    private apiService: ApiService
  ) { }

  /**
   * Verifica si el usuario actual puede usar el editor avanzado
   * Los usuarios (role='user') con el claim Y de la compañía específica
   * Los members (role='member') solo necesitan el claim de Firebase
   */
  async canUseAdvancedEditor(): Promise<boolean> {
    try {
      const userData = await this.authService.getUserData();
      if (!userData) {
        return false;
      }

      const token = await userData.getIdTokenResult(true);
      const hasFirebaseClaim = token.claims['can_use_advanced_editor'] === true;
      const userRole = token.claims['role'];

      if (!hasFirebaseClaim) {
        return false;
      }

      if (userRole === 'member') {
        return true;
      }

      if (userRole === 'user') {
        return true;
      }

      const userStatus = await this.apiService.getUserStatus();
      const isAdmin = userStatus.isAdmin === true;

      return hasFirebaseClaim && isAdmin;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si el usuario actual puede editar configuraciones
   */
  async canEdit(): Promise<boolean> {
    try {
      const userData = await this.authService.getUserData();
      if (!userData) return false;

      const token = await userData.getIdTokenResult();
      return token.claims['can_edit'] === true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si el usuario actual puede acceder al CRM
   */
  async canAccessCrm(): Promise<boolean> {
    try {
      const userData = await this.authService.getUserData();
      if (!userData) {
        return false;
      }

      const token = await userData.getIdTokenResult(true);
      const hasFirebaseClaim = token.claims['can_access_crm'] === true;

      return hasFirebaseClaim;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si el usuario actual puede gestionar la cuenta (equipo, roles,
   * tokens...): el owner de la cuenta, o un member promocionado a
   * account_role='admin'.
   */
  async isAccountAdmin(): Promise<boolean> {
    try {
      const userData = await this.authService.getUserData();
      if (!userData) {
        return false;
      }

      const token = await userData.getIdTokenResult(true);
      const role = token.claims['role'];
      const accountRole = token.claims['account_role'];

      return role === 'user' || accountRole === 'admin';
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si el usuario actual es un usuario principal (no miembro)
   */
  async isMainUser(): Promise<boolean> {
    try {
      const userData = await this.authService.getUserData();
      if (!userData) {
        return false;
      }

      const token = await userData.getIdTokenResult(true);
      const userRole = token.claims['role'];

      const isMain = Boolean(userRole && userRole !== 'member');

      return isMain;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si el usuario actual puede ver el panel de billable minutes
   * - Los usuarios principales (role='user') siempre pueden ver el panel
   * - Los members (role='member') solo pueden ver el panel si tienen el claim can_view_billable_minutes
   */
  async canViewBillableMinutes(): Promise<boolean> {
    try {
      const userData = await this.authService.getUserData();
      if (!userData) {
        return false;
      }

      const token = await userData.getIdTokenResult(true);
      const userRole = token.claims['role'];
      const hasFirebaseClaim = token.claims['can_view_billable_minutes'] === true;

      if (userRole === 'user') {
        return true;
      }

      if (userRole === 'member') {
        return hasFirebaseClaim;
      }

      const userStatus = await this.apiService.getUserStatus();

      if (userStatus.isAdmin === true) {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }
}
